import React, { useEffect, useState } from "react";
import { Grid3x3, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  BARQUETTES_PER_EMPLACEMENT,
  DEFAULT_ALL_EMPS,
  parseCustomEmplacementList,
} from "./constants";

export default function EmplacementsDialog({ open, onOpenChange, target, lots, productMap, onUpdated }) {
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [customList, setCustomList] = useState("");
  const [localTarget, setLocalTarget] = useState(target);

  useEffect(() => {
    setLocalTarget(target);
    if (!target) return;
    const emps = (target.emplacements || []).slice().sort((a, b) => a - b);
    const caps = target.emplacement_capacities || {};
    if (emps.length > 0) {
      setRangeStart(String(emps[0]));
      setRangeEnd(String(emps[emps.length - 1]));
      setCustomList(
        emps
          .map((n) => {
            const c = caps[String(n)];
            return c && c !== BARQUETTES_PER_EMPLACEMENT ? `${n}×${c}` : String(n);
          })
          .join(", ")
      );
      const isContiguous = emps[emps.length - 1] - emps[0] + 1 === emps.length;
      const hasCustomCaps = Object.keys(caps).length > 0;
      setCustomMode(!isContiguous || hasCustomCaps);
    } else {
      setRangeStart("10");
      setRangeEnd("58");
      setCustomList("");
      setCustomMode(false);
    }
  }, [target]);

  if (!target || !localTarget) return null;

  const getBarquetteLot = (depotId, emp, barq) =>
    lots.find(
      (l) => l.distributor_id === depotId && l.emplacement_number === emp && l.barquette_index === barq
    );

  const applyRange = async () => {
    const s = Number(rangeStart);
    const e = Number(rangeEnd);
    if (!Number.isInteger(s) || !Number.isInteger(e)) {
      toast.error("Saisissez des nombres entiers");
      return;
    }
    if (s < 1 || e < s || e - s > 500) {
      toast.error("Plage invalide (1 ≤ début ≤ fin et max 500 emplacements)");
      return;
    }
    try {
      const { data } = await api.put(`/distributors/${localTarget.id}/emplacements/range`, { start: s, end: e });
      setLocalTarget(data);
      onUpdated(data);
      toast.success(`Plage redéfinie : ${s} → ${e} (${data.emplacements.length} emplacements)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de modifier la plage");
    }
  };

  const applyCustomList = async () => {
    const items = parseCustomEmplacementList(customList);
    if (items.length === 0) {
      toast.error("Aucun numéro valide trouvé");
      return;
    }
    const payload = items.some((it) => it.capacity !== null)
      ? { items: items.map((it) => ({ number: it.number, capacity: it.capacity ?? BARQUETTES_PER_EMPLACEMENT })) }
      : { numbers: items.map((it) => it.number) };
    try {
      const { data } = await api.put(`/distributors/${localTarget.id}/emplacements/list`, payload);
      setLocalTarget(data);
      onUpdated(data);
      toast.success(`Liste personnalisée appliquée (${data.emplacements.length} emplacements)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'appliquer la liste");
    }
  };

  const removeEmp = async (number) => {
    try {
      const { data } = await api.delete(`/distributors/${localTarget.id}/emplacements/${number}`);
      setLocalTarget(data);
      onUpdated(data);
      toast.success(`Emplacement ${number} supprimé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const addEmp = async (number) => {
    try {
      const { data } = await api.post(`/distributors/${localTarget.id}/emplacements/${number}`);
      setLocalTarget(data);
      onUpdated(data);
      toast.success(`Emplacement ${number} réactivé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const emps = (localTarget.emplacements || []).slice().sort((a, b) => a - b);
  const caps = localTarget.emplacement_capacities || {};
  const totalBarq = emps.reduce((s, n) => s + (caps[String(n)] || BARQUETTES_PER_EMPLACEMENT), 0);
  const occupied = lots.filter((l) => l.distributor_id === localTarget.id && l.emplacement_number).length;
  const showDefault = emps.length === 0 ? DEFAULT_ALL_EMPS : emps;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Grid3x3 className="w-5 h-5 text-[#1F4E3D]" />
            Emplacements — {localTarget.name}
          </DialogTitle>
          <DialogDescription>
            {`${emps.length} emplacement${emps.length > 1 ? "s" : ""} · ${totalBarq} barquettes au total · ${occupied} occupée${occupied > 1 ? "s" : ""}`}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3 space-y-3" data-testid="range-configurator">
          <div className="flex items-center gap-3 text-xs">
            <button
              onClick={() => setCustomMode(false)}
              className={`px-2.5 py-1 rounded font-medium transition-colors ${
                !customMode
                  ? "bg-[#1F4E3D] text-white"
                  : "bg-white text-[#575A57] border border-[#E5E5E0]"
              }`}
              data-testid="mode-range-btn"
            >
              Plage continue
            </button>
            <button
              onClick={() => setCustomMode(true)}
              className={`px-2.5 py-1 rounded font-medium transition-colors ${
                customMode
                  ? "bg-[#1F4E3D] text-white"
                  : "bg-white text-[#575A57] border border-[#E5E5E0]"
              }`}
              data-testid="mode-list-btn"
            >
              Liste personnalisée
            </button>
          </div>

          {!customMode ? (
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">Début</Label>
                <Input
                  type="number"
                  min="1"
                  max="9999"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="w-24 font-mono"
                  data-testid="range-start"
                />
              </div>
              <div className="text-[#A3A6A3] pb-2">→</div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">Fin</Label>
                <Input
                  type="number"
                  min="1"
                  max="9999"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="w-24 font-mono"
                  data-testid="range-end"
                />
              </div>
              <Button onClick={applyRange} variant="outline" size="sm" className="ml-2" data-testid="apply-range-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Appliquer la plage
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-wider text-[#575A57] block">
                Numéros (séparés par virgule, espace ou retour ligne)
              </Label>
              <Textarea
                value={customList}
                onChange={(e) => setCustomList(e.target.value)}
                placeholder="Ex : 10, 13, 16, 20, 23, 26, 30, 33, 36, 40, 43, 46, 50, 53, 56"
                rows={3}
                className="font-mono text-sm"
                data-testid="custom-list"
              />
              {(() => {
                const items = parseCustomEmplacementList(customList);
                const total = items.reduce((s, it) => s + (it.capacity ?? BARQUETTES_PER_EMPLACEMENT), 0);
                const customCaps = items.filter(
                  (it) => it.capacity !== null && it.capacity !== BARQUETTES_PER_EMPLACEMENT
                ).length;
                return (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-[#575A57]">
                      {items.length} numéro{items.length > 1 ? "s" : ""} valide{items.length > 1 ? "s" : ""}
                      {items.length > 0 && ` · ${total} barquettes`}
                      {customCaps > 0 && (
                        <span className="ml-1 text-[#1F4E3D]">
                          · {customCaps} capacité{customCaps > 1 ? "s" : ""} personnalisée
                          {customCaps > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <Button onClick={applyCustomList} variant="outline" size="sm" data-testid="apply-list-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Appliquer la liste
                    </Button>
                  </div>
                );
              })()}
            </div>
          )}
          <p className="text-[11px] text-[#A3A6A3]">
            Définit la liste complète des numéros pour ce dépôt. Les barquettes occupées hors de la nouvelle liste
            bloquent l'opération.
          </p>
        </div>

        <div className="py-4" data-testid="emplacements-grid">
          <div className="grid grid-cols-7 sm:grid-cols-10 gap-2">
            {showDefault.map((n) => {
              const active = (localTarget.emplacements || []).includes(n);
              const capacity = caps[String(n)] || BARQUETTES_PER_EMPLACEMENT;
              const customCap = capacity !== BARQUETTES_PER_EMPLACEMENT;
              const barquetteLots = active
                ? Array.from({ length: capacity }, (_, i) => getBarquetteLot(localTarget.id, n, i + 1))
                : [];
              const occupiedCount = barquetteLots.filter(Boolean).length;
              const isFull = occupiedCount === capacity;
              const hasSome = occupiedCount > 0;
              return (
                <div
                  key={n}
                  data-testid={`emp-${n}`}
                  data-active={active}
                  className={`
                    relative rounded-md text-sm font-mono border transition-all
                    ${
                      active
                        ? isFull
                          ? "bg-[#1F4E3D] border-[#1F4E3D] text-white"
                          : hasSome
                          ? "bg-[#D9EAD3] border-[#B4D3A7] text-[#1F4E3D]"
                          : "bg-[#EAF3EE] border-[#C5DBCF] text-[#1F4E3D]"
                        : "bg-[#F4F4F0] border-dashed border-[#E5E5E0] text-[#A3A6A3]"
                    }
                  `}
                >
                  <button
                    onClick={() => (active ? removeEmp(n) : addEmp(n))}
                    className="w-full px-1.5 pt-2 pb-1 text-center hover:opacity-80 transition-opacity"
                    title={
                      active
                        ? `Emplacement ${n} (capacité ${capacity}) — clic pour supprimer`
                        : `Emplacement ${n} supprimé — clic pour réactiver`
                    }
                    disabled={active && hasSome}
                  >
                    <div className="font-semibold leading-none text-xs flex items-center justify-center gap-0.5">
                      {n}
                      {customCap && <span className="text-[8px] opacity-70 font-normal">(×{capacity})</span>}
                    </div>
                    {active ? (
                      <div className="text-[9px] opacity-80 mt-1">
                        {occupiedCount}/{capacity}
                      </div>
                    ) : (
                      <div className="text-[9px] opacity-80 mt-1">—</div>
                    )}
                  </button>
                  {active && (
                    <div
                      className="grid gap-0.5 px-1 pb-1"
                      style={{ gridTemplateColumns: `repeat(${Math.min(capacity, 5)}, minmax(0, 1fr))` }}
                    >
                      {Array.from({ length: capacity }, (_, i) => i + 1).map((b) => {
                        const lot = barquetteLots[b - 1];
                        const product = lot ? productMap[lot.product_id] : null;
                        return (
                          <div
                            key={b}
                            data-testid={`barq-${n}-${b}`}
                            data-occupied={!!lot}
                            title={lot ? `B${b} — ${product?.name || "Lot"} · ${lot.lot_number}` : `B${b} — libre`}
                            className={`h-2 rounded-sm ${
                              lot
                                ? isFull
                                  ? "bg-white/80"
                                  : "bg-[#1F4E3D]"
                                : "bg-white/60 border border-current opacity-40"
                            }`}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3 text-xs text-[#575A57] flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#EAF3EE] border border-[#C5DBCF]"></div>
            Libre
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#D9EAD3] border border-[#B4D3A7]"></div>
            Partiel
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#1F4E3D]"></div>
            Plein
          </div>
          <div className="ml-auto font-mono">{BARQUETTES_PER_EMPLACEMENT} barquettes / emplacement</div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-emplacements-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
